import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { VoucherType } from 'src/features/voucherTypes/utils/types';
import { useDeleteVoucherType } from 'src/features/voucherTypes/api/useDeleteVoucherType';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { LIST_VOUCHER_TYPES_RESOURCE } from 'src/features/voucherTypes/api/useListVoucherTypes';

interface DeleteVoucherTypeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedVoucherType: VoucherType;
    children?: React.ReactNode;
}

function DeleteVoucherTypeFormModal<TData>({ selectedVoucherType, children }: DeleteVoucherTypeFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const { mutate: deleteVoucherType } = useDeleteVoucherType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_VOUCHER_TYPES_RESOURCE] });
            setIsModalOpen(false);
            navigate('/voucher-types');
            toast({
                title: formatMessage(voucherTypesMessages.deleteVoucherTypeSuccess),
                description: formatMessage(voucherTypesMessages.deleteVoucherTypeSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...voucherTypesMessages.deleteVoucherType },
                subTitle: {
                    ...voucherTypesMessages.deleteVoucherTypeSubtitle,
                    values: {
                        name: selectedVoucherType.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...voucherTypesMessages.confirmDeleteVoucherType },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: voucherTypesMessages.deleteVoucherType,
                callbackFn: () => {
                    return (
                        deleteVoucherType({
                            voucherTypeId: selectedVoucherType.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteVoucherTypeFormModal;
